import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminAbout() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/about")
      .then((r) => setForm(r.data))
      .catch(() => toast.error("取得に失敗しました"));
  }, []);

  if (!form) {
    return <p className="font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>;
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setRow = (section, i, key) => (e) =>
    setForm((f) => ({
      ...f,
      [section]: f[section].map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)),
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...payload } = form;
      await api.put("/admin/about", payload);
      toast.success("保存しました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-about-page">
      <h1 className="font-mincho text-2xl font-bold text-white">私たちについて の編集</h1>
      <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">ABOUT PAGE MANAGEMENT</p>

      <form onSubmit={save} className="mt-8 max-w-3xl space-y-10">
        <section className="border border-line bg-panel p-6">
          <h2 className="font-mincho text-lg font-bold text-white">企業理念</h2>
          <div className="mt-5 space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="大見出し（前半）">
                <input data-testid="about-philosophy-title" value={form.philosophy_title} onChange={set("philosophy_title")} className={inputCls} />
              </Field>
              <Field label="大見出し（オレンジ強調部分）">
                <input data-testid="about-philosophy-accent" value={form.philosophy_accent} onChange={set("philosophy_accent")} className={inputCls} />
              </Field>
              <Field label="大見出し（後半）">
                <input data-testid="about-philosophy-title-end" value={form.philosophy_title_end} onChange={set("philosophy_title_end")} className={inputCls} />
              </Field>
            </div>
            <Field label="説明文">
              <textarea data-testid="about-philosophy-body" rows={4} value={form.philosophy_body} onChange={set("philosophy_body")} className={`resize-y leading-7 ${inputCls}`} />
            </Field>
          </div>
        </section>

        <section className="border border-line bg-panel p-6">
          <h2 className="font-mincho text-lg font-bold text-white">代表挨拶</h2>
          <div className="mt-5">
            <Field label="挨拶文">
              <textarea data-testid="about-ceo-message" rows={6} value={form.ceo_message} onChange={set("ceo_message")} className={`resize-y leading-7 ${inputCls}`} />
            </Field>
          </div>
        </section>

        <section className="border border-line bg-panel p-6">
          <h2 className="font-mincho text-lg font-bold text-white">ネオ建設の強み（3件）</h2>
          <div className="mt-5 space-y-6">
            {form.strengths.map((s, i) => (
              <div key={i} data-testid={`strength-form-${i}`} className="space-y-3 border border-line bg-ink p-4">
                <p className="font-mono text-[10px] tracking-[0.25em] text-accent">強み {String(i + 1).padStart(2, "0")}</p>
                <Field label="タイトル">
                  <input data-testid={`strength-title-${i}`} value={s.title} onChange={setRow("strengths", i, "title")} className={inputCls} />
                </Field>
                <Field label="短い説明（カードに表示）">
                  <textarea data-testid={`strength-desc-${i}`} rows={2} value={s.desc} onChange={setRow("strengths", i, "desc")} className={`resize-y ${inputCls}`} />
                </Field>
                <Field label="詳細（クリック時のポップアップに表示）">
                  <textarea data-testid={`strength-detail-${i}`} rows={3} value={s.detail} onChange={setRow("strengths", i, "detail")} className={`resize-y ${inputCls}`} />
                </Field>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-line bg-panel p-6">
          <h2 className="font-mincho text-lg font-bold text-white">安全への取り組み（4件）</h2>
          <div className="mt-5 space-y-6">
            {form.safety.map((s, i) => (
              <div key={i} data-testid={`safety-form-${i}`} className="space-y-3 border border-line bg-ink p-4">
                <p className="font-mono text-[10px] tracking-[0.25em] text-accent">取り組み {String(i + 1).padStart(2, "0")}</p>
                <Field label="タイトル">
                  <input data-testid={`safety-title-${i}`} value={s.title} onChange={setRow("safety", i, "title")} className={inputCls} />
                </Field>
                <Field label="詳細（クリック時のポップアップに表示）">
                  <textarea data-testid={`safety-detail-${i}`} rows={3} value={s.detail} onChange={setRow("safety", i, "detail")} className={`resize-y ${inputCls}`} />
                </Field>
              </div>
            ))}
          </div>
        </section>

        <button data-testid="about-save-button" type="submit" disabled={saving} className="w-full bg-accent py-3.5 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
          {saving ? "保存中..." : "保存する"}
        </button>
      </form>
    </div>
  );
}
